import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError, translateFieldErrors } from '@/lib/api-errors';
import {
  formatCheckinProgramRange,
  formatCheckinSessionWhen,
  type CheckinOccurrenceRow,
} from '@/pages/attendance/checkin-occurrence-utils';
import type { CheckinGatheringChoice } from '@/pages/attendance/checkin-calendar-gatherings';
import {
  ensureCheckinOccurrenceId,
  findOccurrenceForGathering,
  isPersistedOccurrenceId,
} from '@/pages/attendance/ensure-checkin-occurrence';
import { useChurchStore } from '@/stores/church-store';

interface KioskOption {
  id: string;
  name: string;
  status: 'unpaired' | 'paired' | 'revoked';
  currentSession: {
    id: string;
    status: 'open' | 'closed';
    gatheringName: string;
  } | null;
}

export type OpenCheckinOccurrence = CheckinOccurrenceRow;

interface OpenCheckinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gathering: CheckinGatheringChoice | null;
  kindLabel: (kind: 'service' | 'program') => string;
  locale: string;
}

const isScreenBusy = (row: KioskOption): boolean =>
  row.currentSession?.status === 'open';

export const OpenCheckinDialog = ({
  open,
  onOpenChange,
  gathering,
  kindLabel,
  locale,
}: OpenCheckinDialogProps) => {
  const { t } = useTranslation('attendance');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const submitLock = useRef(false);
  const [selectedKioskIds, setSelectedKioskIds] = useState<string[]>([]);
  const [includeBusyScreens, setIncludeBusyScreens] = useState(false);
  const [gpsAutoCheckInEnabled, setGpsAutoCheckInEnabled] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const campusLocationReady =
    church?.locationLatitude != null && church?.locationLongitude != null;

  const kiosks = useQuery({
    queryKey: ['attendance-kiosks', 'open-checkin'],
    queryFn: () => api.get<{ data: KioskOption[] }>('/attendance/kiosks?pageSize=100'),
    enabled: open,
  });

  const pairedKiosks = useMemo(
    () => (kiosks.data?.data ?? []).filter((row) => row.status === 'paired'),
    [kiosks.data?.data],
  );

  const availableKiosks = useMemo(
    () => pairedKiosks.filter((row) => !isScreenBusy(row)),
    [pairedKiosks],
  );

  const busyKiosks = useMemo(
    () => pairedKiosks.filter((row) => isScreenBusy(row)),
    [pairedKiosks],
  );

  const selectableKiosks = useMemo(
    () => (includeBusyScreens ? pairedKiosks : availableKiosks),
    [availableKiosks, includeBusyScreens, pairedKiosks],
  );

  useEffect(() => {
    if (!open) return;
    setIncludeBusyScreens(false);
    setFieldErrors({});
    setSelectedKioskIds(availableKiosks.map((row) => row.id));
    setGpsAutoCheckInEnabled(gathering?.gpsAutoCheckInEnabled === true && campusLocationReady);
  }, [open, availableKiosks, gathering?.gpsAutoCheckInEnabled, campusLocationReady]);

  useEffect(() => {
    if (includeBusyScreens) return;
    setSelectedKioskIds((current) =>
      current.filter((id) => availableKiosks.some((row) => row.id === id)),
    );
  }, [availableKiosks, includeBusyScreens]);

  const needsReassign = useMemo(
    () => selectedKioskIds.some((id) => busyKiosks.some((row) => row.id === id)),
    [busyKiosks, selectedKioskIds],
  );

  const resolvedOccurrence = useQuery({
    queryKey: ['occurrences', 'resolve', gathering?.programId, gathering?.occurrenceDate],
    queryFn: () => findOccurrenceForGathering(gathering!),
    enabled:
      open &&
      gathering !== null &&
      !isPersistedOccurrenceId(gathering.occurrenceId),
  });

  const effectiveGathering = useMemo((): CheckinGatheringChoice | null => {
    if (!gathering) return null;
    const occurrenceId = isPersistedOccurrenceId(gathering.occurrenceId)
      ? gathering.occurrenceId
      : resolvedOccurrence.data ?? gathering.occurrenceId;
    if (occurrenceId === gathering.occurrenceId) return gathering;
    return { ...gathering, occurrenceId };
  }, [gathering, resolvedOccurrence.data]);

  const openSession = useMutation({
    mutationFn: async (input: {
      gathering: CheckinGatheringChoice;
      kioskIds: string[];
      reassignKiosks: boolean;
      gpsAutoCheckInEnabled: boolean;
    }) => {
      const occurrenceId = await ensureCheckinOccurrenceId(input.gathering);
      return api.post('/attendance/sessions', {
        occurrenceId,
        method: 'qr',
        kioskIds: input.kioskIds,
        reassignKiosks: input.reassignKiosks,
        gpsAutoCheckInEnabled: input.gpsAutoCheckInEnabled,
      });
    },
    onSuccess: () => {
      submitLock.current = false;
      toast.success(t('sessions.opened'));
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['attendance-sessions'] });
      void queryClient.invalidateQueries({ queryKey: ['programs', 'calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences', 'check-in'] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences', 'resolve'] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.details) {
        setFieldErrors(translateFieldErrors(err.details));
      }
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
    onSettled: () => {
      submitLock.current = false;
    },
  });

  if (!effectiveGathering) return null;

  const kind = effectiveGathering.programKind ?? 'service';
  const gatheringName = effectiveGathering.programName?.trim() || kindLabel(kind);
  const multiDayRangeLabel = (start: string, end: string) =>
    t('sessions.multiDayRange', { start, end });
  const sessionWhen = formatCheckinSessionWhen(effectiveGathering, locale);
  const programRange = formatCheckinProgramRange(effectiveGathering, locale, multiDayRangeLabel);
  const leadText = programRange
    ? t('sessions.openDialog.leadMultiDay', {
        name: gatheringName,
        range: programRange,
        sessionWhen,
      })
    : t('sessions.openDialog.lead', { name: gatheringName, when: sessionWhen });

  const canSubmit = selectedKioskIds.length > 0 && !openSession.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('sessions.openDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody contentClassName="flex flex-col gap-4">
          <p className="text-body-sm text-fg-secondary">{leadText}</p>

          {kiosks.isLoading ? (
            <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
          ) : null}

          {!kiosks.isLoading && pairedKiosks.length === 0 ? (
            <div className="rounded-lg border border-border bg-surface-sunken px-4 py-3">
              <p className="text-body-sm text-fg-secondary">{t('sessions.openDialog.noKiosks')}</p>
              <Button variant="outline" size="sm" className="mt-3" asChild>
                <Link to="/attendance/kiosks">{t('sessions.openDialog.registerKiosk')}</Link>
              </Button>
            </div>
          ) : null}

          {!kiosks.isLoading && pairedKiosks.length > 0 && availableKiosks.length === 0 && !includeBusyScreens ? (
            <div className="rounded-lg border border-border bg-surface-sunken px-4 py-3">
              <p className="text-body-sm text-fg-secondary">
                {t('sessions.openDialog.noAvailableScreens')}
              </p>
            </div>
          ) : null}

          {pairedKiosks.length > 0 ? (
            <>
              {busyKiosks.length > 0 ? (
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="includeBusyScreens"
                    checked={includeBusyScreens}
                    onCheckedChange={(checked) => setIncludeBusyScreens(checked === true)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="includeBusyScreens" className="font-normal">
                      {t('sessions.openDialog.includeBusyScreens')}
                    </Label>
                    <p className="text-body-sm text-fg-secondary">
                      {t('sessions.openDialog.includeBusyScreensHint')}
                    </p>
                  </div>
                </div>
              ) : null}

              {selectableKiosks.length > 0 ? (
                <FormField
                  label={t('sessions.openDialog.kiosksLabel')}
                  hint={t('sessions.openDialog.kiosksHint')}
                  error={fieldErrors.kioskIds}
                >
                  {({ id, invalid }) => (
                    <MultiSelectListbox
                      id={id}
                      invalid={invalid}
                      value={selectedKioskIds}
                      onChange={(next) => {
                        setFieldErrors((current) => {
                          if (!current.kioskIds) return current;
                          const { kioskIds: _removed, ...rest } = current;
                          return rest;
                        });
                        setSelectedKioskIds(next);
                      }}
                      options={selectableKiosks.map((row) => ({
                        value: row.id,
                        label: row.name,
                        description: isScreenBusy(row)
                          ? t('sessions.openDialog.screenInUse', {
                              session: row.currentSession!.gatheringName,
                            })
                          : undefined,
                      }))}
                      placeholder={t('sessions.openDialog.kiosksPlaceholder')}
                      popoverPortal="dialog"
                    />
                  )}
                </FormField>
              ) : null}
            </>
          ) : null}

          <SwitchField
            id="open-gps-auto-check-in"
            label={t('sessions.openDialog.gpsAutoCheckIn')}
            description={
              campusLocationReady
                ? t('sessions.openDialog.gpsAutoCheckInHint')
                : t('sessions.openDialog.gpsNeedsCampusLocation')
            }
            checked={gpsAutoCheckInEnabled}
            onCheckedChange={setGpsAutoCheckInEnabled}
            disabled={!campusLocationReady}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            disabled={!canSubmit || openSession.isPending}
            loading={openSession.isPending}
            onClick={() => {
              if (openSession.isPending || submitLock.current) return;
              submitLock.current = true;
              openSession.mutate({
                gathering: effectiveGathering,
                kioskIds: selectedKioskIds,
                reassignKiosks: needsReassign,
                gpsAutoCheckInEnabled,
              });
            }}
          >
            {t('sessions.open')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
